import { get, map, merge, toNumber, uniq } from 'lodash';
import shortId from 'shortid';
import superAgent from 'superagent';
import superAgentCache from 'superagent-cache';
import { broadcast } from '../event';
import Collection from './Collection';
import type { IData } from './Data.interface';
import KeyValueMap from './KeyValueMap';
import RequestParameters from './RequestParameters';
import type { IRequestParameters } from './RequestParameters.interface';

export default class Data implements IData {
  Model: Object;
  ModelName: String;
  Object: Object;
  Collection: Collection;
  Requests: KeyValueMap;
  Total: -1;
  Limit: 0;
  hasErrors: Boolean;
  useCaching: Boolean;

  count = async (request?: String, requestParameters?: IRequestParameters) => {
    const res = await this.request(
      request,
      merge(requestParameters, { query: { isCount: true } })
    );
    return this.Collection.count(res?.body || 0);
  };

  create = async (payload?: Object, requestParameters?: IRequestParameters) => {
    const params = this.params('crud', requestParameters);
    return superAgent('POST', params.url)
      .set(params?.header)
      .query(params?.query)
      .type('application/json')
      .send({ params: payload });
  };

  delete = async (requestParameters?: IRequestParameters) => {
    const params = this.params('crud', requestParameters);
    return superAgent('DELETE', params.url).set(params?.header).query(params?.query);
  };

  duplicate = async (payload?: Object, requestParameters?: IRequestParameters) => {
    const params = this.params('crud', requestParameters);
    return superAgent('PUT', params.url)
      .set(params?.header)
      .query(params?.query)
      .type('application/json')
      .send({ params: payload });
  };

  getCollectionHasMore = () => this.Collection.HasMore || false;
  getCollectionIsSelected = (id: String, selection?: Array<Object>) =>
    this.Collection.isSelected(id, selection);

  getCollectionPage = () => this.Collection.Page || 0;
  getCollectionPageCount = () => this.Collection.PageCount || 0;
  getCollectionPageCurrent = () => this.Collection.PageCurrent || 0;
  getCollectionPageOffset = () => this.Collection.PageOffset || 0;
  getCollectionSelection = () => this.Collection.Selection || [];
  getCollectionSize = () => this.Collection.Size || 0;
  getCollectionData = () => this.Collection.Data || [];
  getCollectionDataLength = () => this.Collection.Data?.length || 0;
  getCollectionDataIds = (uniqId) => uniq(map(this.Collection.Data, uniqId || 'id'));
  getRequestsFilterName = (request?: String) =>
    get(this.Requests.get(request), 'query.filterName');
  getRequestsIdFilter = (request?: String) => get(this.Requests.get(request), 'query.idFilter');
  getRequestsIsDescending = (request?: String) =>
    get(this.Requests.get(request), 'query.isDescending');
  getRequestsOrderBy = (request?: String) => get(this.Requests.get(request), 'query.orderBy');
  getRequestsStartingWith = (request?: String) =>
    get(this.Requests.get(request), 'query.startingWith');
  getWillBeTaxing = () => this.Total > this.Limit;

  init = async (request?: String, requestParameters?: IRequestParameters) => {
    if (this.Total === -1) {
      await this.total(request);
    }
    if (this.Collection.Size === -1) {
      await this.count(request, requestParameters);
    }
    if (
      !this.hasErrors &&
      this.Collection.Size > 0 &&
      this.Collection.PageCurrent < this.Collection.PageCount
    ) {
      const res = await this.page(request, requestParameters);
      if (!res) {
        this.hasErrors = true;
      }
    }
  };

  decache = (use: Boolean = false) => (use ? { query: { cache: shortId.generate() } } : {});

  load = async (
    request?: String,
    requestParameters?: IRequestParameters,
    decache: Boolean = false
  ) => {
    const res = await this.request(
      request,
      merge(requestParameters, { query: { isCount: false, isSum: false } }),
      null,
      decache
    );
    return this.Collection.append(res?.body, this.Model, this.ModelName);
  };

  page = async (
    request?: String,
    requestParameters?: IRequestParameters,
    decache: Boolean = false
  ) => {
    return this.load(
      request,
      merge(requestParameters, {
        query: { limit: this.Collection.Page, offset: this.Collection.PageOffset }
      }),
      decache
    );
  };

  params = (
    request?: String,
    requestParameters?: IRequestParameters,
    decache: Boolean = false,
    reset: Boolean = false
  ) => {
    if (this.useCaching && !decache) {
      superAgentCache(superAgent, {
        type: 'cache-module',
        storage: 'local',
        defaultExpiration: 60
      });
    }
    const requestParametersDefault = this.Requests.get(request);
    return new RequestParameters(
      merge({}, requestParametersDefault, requestParameters, this.decache(decache)),
      this.getWillBeTaxing(),
      reset
    );
  };

  read = async (requestParameters?: IRequestParameters) => {
    const params = this.params('crud', requestParameters);
    return superAgent('GET', params.url).set(params?.header).query(params?.query);
  };

  reset = async (
    request?: String,
    requestParameters?: IRequestParameters,
    decache: Boolean = false
  ) => {
    this.Requests.update(request, merge({}, requestParameters, this.decache(decache)));
    this.Collection = new Collection();
  };

  request = async (
    request?: String,
    requestParameters?: IRequestParameters,
    payload?: Object,
    decache: Boolean = false,
    reset: Boolean = false
  ) => {
    const params = this.params(request, requestParameters, decache, reset);
    const payloadData = params?.useQueryAsPayload
      ? merge(requestParameters.query, payload)
      : payload;
    if (payloadData) {
      return superAgent(params.method, params.url)
        .set(params?.header)
        .query(params?.useQueryAsPayload ? null : params?.query)
        .type('application/json')
        .send({ params: payloadData });
    }
    return superAgent(params.method, params.url).set(params?.header).query(params?.query);
  };

  setCollectionSelection = (id: String) => this.Collection.select(id);

  total = async (request?: String) => {
    const res = await this.request(
      request,
      { query: { isActive: true, isCount: true } },
      null,
      false,
      true
    );
    this.Total = toNumber(res?.body || 0);
  };

  update = async (payload?: Object, requestParameters?: IRequestParameters) => {
    const params = this.params('crud', requestParameters);
    return superAgent('PATCH', params.url)
      .set(params?.header)
      .query(params?.query)
      .type('application/json')
      .send({ params: payload });
  };

  /**
   * Constructor
   * @param {IData} data
   */
  constructor(data?: IData) {
    this.Model = data?.Model;
    this.ModelName = data?.ModelName || this?.Model?.constructor?.name || '';
    this.Object = data?.Object;
    this.Collection = new Collection(data?.Collection);
    this.Requests = new KeyValueMap(data?.Requests);
    this.Total = data?.Total || -1;
    this.Limit = data?.Limit || 25000;
    this.hasErrors = data?.hasErrors === true;
    this.useCaching = data?.useCaching === true;
  }
}
